import React, { useContext, useState } from "react";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import H24 from "../../../../assets/svg/24hour.svg";
import { DeleteModal } from "../../../modals/Delete";
import "./WorkTime.css";
import { translations } from "../../../../utils/lang";
import { Button, Input } from "../../../ui";
import { Copy, Trash2 } from "../../../ui/icons";

const defaultDays = {
  sunday: { morning: { from: "", to: "" }, evening: { from: "", to: "" } },
  monday: { morning: { from: "", to: "" }, evening: { from: "", to: "" } },
  tuesday: { morning: { from: "", to: "" }, evening: { from: "", to: "" } },
  wednesday: { morning: { from: "", to: "" }, evening: { from: "", to: "" } },
  thursday: { morning: { from: "", to: "" }, evening: { from: "", to: "" } },
  friday: { morning: { from: "", to: "" }, evening: { from: "", to: "" } },
  saturday: { morning: { from: "", to: "" }, evening: { from: "", to: "" } },
};

export default function WorkTime({ branches, setBranches, branchIndex, onSave, restaurantId }) {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const resource = { restaurantId };
  const [isDirty, setIsDirty] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const addWorkTime = () => {
    const updated = [...(branches || [])];

    if (!updated?.[branchIndex]?.workTime) {
      updated[branchIndex].workTime = [];
    }

    updated[branchIndex].workTime.push({
      name: "",
      days: { ...defaultDays },
    });

    setBranches(updated);
    setIsDirty(true);
  };

  const updateWorkTime = (wtIndex, key, value) => {
    const updated = [...(branches || [])];
    updated[branchIndex].workTime[wtIndex][key] = value;
    setBranches(updated);
    setIsDirty(true);
  };

  const updateWorkTimeDay = (wtIndex, day, period, timeType, value) => {
    const updated = [...(branches || [])];
    updated[branchIndex].workTime[wtIndex].days[day][period][timeType] = value;
    setBranches(updated);
    setIsDirty(true);
  };

  const handleConfirmedDelete = (wtIndex) => {
    if (!can("restaurant.branch.workTime.delete", resource)) return;
    const updated = [...(branches || [])];
    updated[branchIndex].workTime = updated[branchIndex].workTime.filter(
      (_, i) => i !== wtIndex
    );
    setBranches(updated);
    setIsDirty(true);
    setDeleteTarget(null);
  };

  const confirmDelete = (index) => {
    setDeleteTarget(index);
  };

  const copyTimeToAllDays = (wtIndex) => {
    const updated = [...(branches || [])];
    const sourceDay = Object.keys(updated[branchIndex].workTime[wtIndex].days)[0];
    const source = updated[branchIndex].workTime[wtIndex].days[sourceDay];

    Object.keys(updated[branchIndex].workTime[wtIndex].days).forEach((day) => {
      updated[branchIndex].workTime[wtIndex].days[day] = {
        morning: { ...source.morning },
        evening: { ...source.evening },
      };
    });

    setBranches(updated);
    setIsDirty(true);
  };

  const full24hWorking = (wtIndex) => {
    const updated = [...(branches || [])];
    const workTimeDays = updated[branchIndex].workTime[wtIndex].days;

    Object.keys(workTimeDays).forEach((day) => {
      workTimeDays[day] = {
        morning: { from: "00:00", to: "23:59" },
        evening: { from: "", to: "" },
      };
    });

    setBranches(updated);
  };

  const copyWorkTimeToAllBranches = () => {
    const updated = branches.map((branch, i) => {
      if (i === branchIndex) return branch;

      return {
        ...branch,
        workTime: [
          ...branches[branchIndex].workTime.map((wt) => ({
            name: wt.name,
            days: JSON.parse(JSON.stringify(wt.days)),
          })),
        ],
      };
    });

    setBranches(updated);
    setIsDirty(true);
  };

  const handleSave = () => {
    if (!can("restaurant.branch.workTime.update", resource)) return;
    onSave();
    setIsDirty(false);
  };

  const canEdit = can("restaurant.branch.workTime.update", resource);

  return (
    <div className="worktime-main-container">
      {canEdit && (
        <div className="worktime-btns">
          <Button type="button" onClick={addWorkTime} variant="primary" appearance="soft">
            {translations[lang].WorkTime.addWorkTime}
          </Button>

          <Button
            type="button"
            onClick={copyWorkTimeToAllBranches}
            variant="primary"
            appearance="soft"
          >
            {translations[lang].WorkTime.copyToAllBranches}
          </Button>

          <Button
            type="button"
            disabled={!isDirty}
            onClick={handleSave}
            variant="primary"
            appearance={isDirty ? "filled" : "soft"}
          >
            {translations[lang].WorkTime.save}
          </Button>
        </div>
      )}

      {canEdit ? (
        <div className="worktime-container">
          {branches?.[branchIndex].workTime?.map((wt, wtIndex) => (
            <div key={wtIndex} className="worktime-entry">
              <Input
                type="text"
                placeholder="Work Time Name"
                value={wt.name}
                onChange={(e) => updateWorkTime(wtIndex, "name", e.target.value)}
              />

              <div className="worktime-day-grid">
                {Object.entries(wt.days).map(([day, time]) => (
                  <div key={day} className="worktime-day-row">
                    <label>{translations[lang].WorkTime.days[day].toUpperCase()}</label>

                    <div className="worktime-subgrid">
                      <span>{translations[lang].WorkTime.from}</span>
                      <Input
                        type="time"
                        className="worktime-time-input"
                        value={time?.morning?.from || ""}
                        onChange={(e) =>
                          updateWorkTimeDay(
                            wtIndex,
                            day,
                            "morning",
                            "from",
                            e.target.value
                          )
                        }
                      />
                      <span>{translations[lang].WorkTime.to}</span>
                      <Input
                        type="time"
                        className="worktime-time-input"
                        value={time?.morning?.to || ""}
                        onChange={(e) =>
                          updateWorkTimeDay(
                            wtIndex,
                            day,
                            "morning",
                            "to",
                            e.target.value
                          )
                        }
                      />
                    </div>

                    <div className="worktime-subgrid">
                      <span>{translations[lang].WorkTime.from}</span>
                      <Input
                        type="time"
                        className="worktime-time-input"
                        value={time?.evening?.from || ""}
                        onChange={(e) =>
                          updateWorkTimeDay(
                            wtIndex,
                            day,
                            "evening",
                            "from",
                            e.target.value
                          )
                        }
                      />
                      <span>{translations[lang].WorkTime.to}</span>
                      <Input
                        type="time"
                        className="worktime-time-input"
                        value={time?.evening?.to || ""}
                        onChange={(e) =>
                          updateWorkTimeDay(
                            wtIndex,
                            day,
                            "evening",
                            "to",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="worktime-controls">
                <Button
                  type="button"
                  onClick={() => copyTimeToAllDays(wtIndex)}
                  variant="primary"
                  appearance="soft"
                  icon={<Copy size={18} />}
                />

                <Button
                  type="button"
                  onClick={() => full24hWorking(wtIndex)}
                  variant="success"
                  appearance="soft"
                  className="worktime-24h-ui-btn"
                  icon={<img src={H24} alt="24h" />}
                />

                <div className="worktime-controls-space"></div>

                {can("restaurant.branch.workTime.delete", resource) && (
                  <Button
                    type="button"
                    onClick={() => confirmDelete(wtIndex)}
                    variant="danger"
                    appearance=""
                    icon={<Trash2 size={18} />}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="worktime-container-display">
          {branches?.[branchIndex].workTime?.map((wt, wtIndex) => (
            <div key={wtIndex} className="worktime-entry">
              <p className="worktime-display-name">{wt.name}</p>

              <div className="worktime-day-grid">
                {Object.entries(wt.days).map(([day, time]) => (
                  <div key={day} className="worktime-day-row">
                    <label>{translations[lang].WorkTime.days[day].toUpperCase()}</label>

                    <div className="worktime-subgrid">
                      <span>{translations[lang].WorkTime.from}</span>
                      <p className="worktime-display-time">{time?.morning?.from}</p>
                      <span>{translations[lang].WorkTime.to}</span>
                      <p className="worktime-display-time">{time?.morning?.to}</p>
                    </div>

                    <div className="worktime-subgrid">
                      <span>{translations[lang].WorkTime.from}</span>
                      <p className="worktime-display-time">{time?.evening?.from}</p>
                      <span>{translations[lang].WorkTime.to}</span>
                      <p className="worktime-display-time">{time?.evening?.to}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTarget !== null && (
        <DeleteModal
          id={deleteTarget}
          handleDelete={handleConfirmedDelete}
          cancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}